<script setup lang="ts">
import ActionButton from '@/components/ui/ActionButton.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BookmarkButton from '@/components/ui/BookmarkButton.vue';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import ListItem from '@/components/ui/ListItem.vue';
import OnDemandTag from '@/components/ui/OnDemandTag.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import SkeletonBlock from '@/components/ui/SkeletonBlock.vue';
import dayjs from 'dayjs';
import { computed, onUnmounted, ref, watch } from 'vue';
import type { OnDemandRoute } from '@/@types/on-demand-route';
import type { Bounds } from '@/stores/gtfs';
import {
  connectionLostSince as lostSince,
  isServingOnDemandRoute,
  onDemandServiceState,
} from '@/libs/on-demand-helper';
import { MapFocusType, useAppStore } from '@/stores/app';
import { useDevicesStore } from '@/stores/devices';
import { useGroupStore } from '@/stores/group';
import { useGtfsStore } from '@/stores/gtfs';
import { useOnDemandStore } from '@/stores/on-demand';
import { useClock } from '@/use/clock';

const SINGLE_STOP_MARGIN_DEG = 0.003;

const props = defineProps<{
  onDemandRouteId: string;
}>();

const appStore = useAppStore();
const devicesStore = useDevicesStore();
const groupStore = useGroupStore();
const gtfsStore = useGtfsStore();
const onDemandStore = useOnDemandStore();
const clockNow = useClock();

const sheetPosition = ref<BottomSheetPosition>('intermediate');
const route = ref<OnDemandRoute>();
const loadState = ref<'loading' | 'ready' | 'error'>('loading');

// A route opened meanwhile must not get the answer for the previous one.
let loadRun = 0;

const vehicleCount = computed<number>(
  () =>
    devicesStore.onlineDevices.filter(device => isServingOnDemandRoute(device, props.onDemandRouteId)).length,
);

const serviceState = computed(() =>
  route.value ? onDemandServiceState(route.value, appStore.todayDateGtfs, vehicleCount.value) : undefined,
);

const connectionLostSince = computed<number | undefined>(() =>
  lostSince(
    devicesStore.onlineDevicesAnsweredAt,
    devicesStore.onlineDevicesPollingSince,
    clockNow.value * 1000,
  ),
);

const connectionLostTime = computed<string | undefined>(() =>
  connectionLostSince.value === undefined ? undefined : dayjs(connectionLostSince.value).format('HH:mm'),
);

async function loadRoute() {
  const groupId = groupStore.group?.group_id;
  if (!groupId) return;
  const run = ++loadRun;
  const routeId = props.onDemandRouteId;
  // The list already holds the route: its header shows at once, the detail refreshes it.
  route.value = onDemandStore.getRoute(routeId);
  loadState.value = route.value ? 'ready' : 'loading';
  try {
    const answer = await onDemandStore.fetchRoute(groupId, routeId);
    if (run !== loadRun) return;
    route.value = answer;
    loadState.value = 'ready';
  } catch (e) {
    if (run !== loadRun) return;
    if (route.value) return;
    loadState.value = 'error';
    // An archived route answers 404, as an unknown one: a stale link, not a fault of ours.
    if (!(e instanceof Error && e.message.startsWith('404:')))
      console.warn('On-demand route unavailable:', e);
  }
}

function frameStops(stops: OnDemandRoute['stops']) {
  if (stops.length === 0) {
    appStore.setMapFocus({ type: MapFocusType.BOUNDS, elem: gtfsStore.networkMapBounds });
    return;
  }
  // Widened around a single stop, which would otherwise zoom the map in to the maximum.
  const margin = stops.length === 1 ? SINGLE_STOP_MARGIN_DEG : 0;
  const bounds: Bounds = {
    north: Math.max(...stops.map(stop => stop.stop_lat)) + margin,
    south: Math.min(...stops.map(stop => stop.stop_lat)) - margin,
    east: Math.max(...stops.map(stop => stop.stop_lon)) + margin,
    west: Math.min(...stops.map(stop => stop.stop_lon)) - margin,
  };
  appStore.setMapFocus({ type: MapFocusType.BOUNDS, elem: bounds });
}

watch(
  () => [props.onDemandRouteId, groupStore.group?.group_id],
  () => {
    // No shape, and none of the GTFS stops: the map only draws this route's own stops.
    gtfsStore.setShownShapes({});
    gtfsStore.setShownStops({});
    loadRoute();
  },
  { immediate: true },
);

watch(
  route,
  (current, previous) => {
    onDemandStore.shownRoute = current;
    if (current && current.route_id !== previous?.route_id) frameStops(current.stops);
  },
  { immediate: true },
);

watch(
  connectionLostSince,
  since => {
    onDemandStore.vehiclesStale = since !== undefined;
  },
  { immediate: true },
);

onUnmounted(() => {
  if (onDemandStore.shownRoute?.route_id === props.onDemandRouteId) onDemandStore.shownRoute = undefined;
  onDemandStore.vehiclesStale = false;
});
</script>

<template>
  <div class="on-demand-panel">
    <BottomSheet v-model:position="sheetPosition" @update:height="appStore.setMapBottomInset">
      <template #header>
        <div v-if="loadState === 'loading'" class="on-demand-panel__header" aria-busy="true">
          <SkeletonBlock class="on-demand-panel__skeleton-tag" />
          <SkeletonBlock class="on-demand-panel__skeleton-name" />
          <SkeletonBlock shape="rectangle" class="on-demand-panel__skeleton-info" />
        </div>

        <EmptyState
          v-else-if="loadState === 'error'"
          class="on-demand-panel__error"
          :title="$t('onDemand.loadError.title')"
          :description="$t('onDemand.loadError.description')"
        >
          <template #actions>
            <ActionButton
              type="primary"
              size="large"
              :label="$t('onDemand.loadError.retry')"
              @click="loadRoute"
            />
          </template>
        </EmptyState>

        <div v-else-if="route" class="on-demand-panel__header">
          <div class="on-demand-panel__identity">
            <div class="on-demand-panel__titles">
              <div class="on-demand-panel__tags">
                <RouteBadge :route="route" size="medium" />
                <OnDemandTag size="medium" />
              </div>
              <div class="on-demand-panel__name">{{ route.route_long_name }}</div>
            </div>
            <span class="on-demand-panel__bookmark">
              <BookmarkButton :element="route" />
            </span>
          </div>

          <div class="on-demand-panel__explanation">
            <BaseIcon name="info" class="on-demand-panel__explanation-icon" />
            <span class="on-demand-panel__explanation-text">{{ $t('onDemand.explanation') }}</span>
          </div>
        </div>
      </template>

      <template v-if="loadState === 'loading'">
        <div class="on-demand-panel__status">
          <SkeletonBlock class="on-demand-panel__skeleton-status" />
        </div>
      </template>

      <template v-else-if="loadState === 'ready' && route">
        <div class="on-demand-panel__status">
          <span v-if="connectionLostTime" class="on-demand-panel__pill">
            {{ $t('onDemand.connectionLost', { time: connectionLostTime }) }}
          </span>
          <span v-else-if="serviceState === 'live'" class="on-demand-panel__pill on-demand-panel__pill--live">
            <BaseIcon name="rss" :stroke-width="3" class="on-demand-panel__pill-icon" />
            {{ $tc('onDemand.liveVehicles', vehicleCount) }}
          </span>
          <span v-else class="on-demand-panel__pill">
            {{ serviceState === 'noVehicle' ? $t('onDemand.noVehicle') : $t('onDemand.noServiceToday') }}
          </span>
        </div>

        <ListItem
          v-for="stop in route.stops"
          :key="stop.stop_id"
          class="on-demand-panel__stop"
          :label="stop.stop_name"
        />
      </template>
    </BottomSheet>
  </div>
</template>

<style lang="scss">
.on-demand-panel {
  @include sheet-host;

  &__header {
    border-bottom: 1px solid $color-border-light;
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    padding: $spacing-4 $spacing-4 $spacing-5;
  }

  &__error {
    border-bottom: 1px solid $color-border-light;
  }

  &__identity {
    align-items: center;
    display: flex;
    gap: $spacing-4;
  }

  &__titles {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }

  &__tags {
    align-items: center;
    display: flex;
    gap: $spacing-2;
    min-width: 0;
  }

  &__name {
    color: $color-neutral-highest;
    display: -webkit-box;
    font: 600 20px / 28px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &__bookmark {
    @include icon-pill;
  }

  // The frame of the route view's disruption banner, on a neutral background.
  &__explanation {
    align-items: flex-start;
    background-color: $color-neutral-lower;
    border-radius: 16px;
    color: $color-text-dark;
    display: flex;
    gap: $spacing-2;
    padding: $spacing-4;
  }

  &__explanation-icon {
    flex-shrink: 0;
    font-size: 24px;
  }

  &__explanation-text {
    flex: 1;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    min-width: 0;
  }

  &__status {
    display: flex;
    padding: $spacing-4 $spacing-5;
  }

  &__pill {
    align-items: center;
    background-color: $color-neutral-lower;
    border-radius: $radius-tag;
    color: $color-text-dark;
    display: inline-flex;
    font: $label-medium;
    gap: $spacing-1;
    letter-spacing: $label-medium-spacing;
    padding: 0 $spacing-1;

    &--live {
      background-color: $color-primary-brand-lowest;
      color: $color-primary-highter;
    }
  }

  &__pill-icon {
    color: $color-primary;
    font-size: 20px;
    transform: rotate(270deg);
  }

  &__stop {
    padding-bottom: $spacing-1;
    padding-top: $spacing-1;
  }

  &__skeleton-tag {
    width: 56px;
  }

  &__skeleton-name {
    width: 70%;
  }

  &__skeleton-info {
    height: 92px;
  }

  &__skeleton-status {
    width: 60%;
  }
}
</style>
